import { useState, useRef, useMemo, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Zap,
  Target,
  ChevronDown,
  ChevronUp,
  Activity,
  LayoutList,
  LayoutGrid,
  X
} from "lucide-react";
import { useApp } from "@/context/AppCtx";
import { useStrategist, type StrategyRecommendation } from "@/hooks/useStrategist";
import { MiniCandleChart } from "@/components/MiniCandleChart";
import { normalizeCoinImageUri } from "@/lib/imageUtils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { fetchWalletPosition } from "@/lib/walletPositionFetcher";

// ── Lily Row ──────────────────────────────────────────────────────────────────

function LilySignalGridRow({ signals, caMintInput, onOpenToken, onRemove }: { signals: StrategyRecommendation[], caMintInput: string, onOpenToken: (mint: string) => void, onRemove: (mint: string) => void }) {
  return (
    <div className="flex w-full px-2 py-1 gap-2 overflow-hidden items-center justify-start h-[120px]">
      {signals.map((signal) => {
        const isSelected = caMintInput === signal.mint;
        const displayImage = normalizeCoinImageUri(signal.imageUri, signal.mint);
        
        return (
          <div
            key={signal.id}
            onClick={() => onOpenToken(signal.mint)}
            className={`group flex-1 min-w-0 max-w-[120px] h-full relative flex flex-col items-center justify-center p-2 rounded-xl border bg-black/40 cursor-pointer transition-all hover:scale-105 hover:z-10 ${
              isSelected ? "border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.2)] bg-emerald-500/10" : "border-white/5 hover:border-emerald-500/30"
            }`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(signal.mint); }}
              className="absolute top-1 right-1 p-1 hover:bg-rose-500/20 rounded-full text-white/30 hover:text-rose-400 transition-all opacity-0 group-hover:opacity-100 bg-black/60 z-20"
              title="Remove"
            >
              <X className="size-3" />
            </button>
            <img src={displayImage || undefined} className="size-10 md:size-12 rounded-full border border-white/10 mb-2 object-cover bg-black/50" />
            <span className="text-[10px] font-black text-white truncate w-full text-center leading-none">{signal.coin}</span>
            <span className="text-[9px] font-mono text-emerald-400 mt-1 leading-none">{signal.bondingPct}%</span>
          </div>
        );
      })}
    </div>
  );
}


export function LiveSignalsPanel({ onOpenToken }: { onOpenToken: (mint: string) => void }) {
  const { caMintInput, strategistThresholds, setStrategistThresholds, layoutMode, setLayoutMode, searchHistory, favorites, removeFromSearchHistory } = useApp() as any;
  const { recommendations: signals } = useStrategist();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useIsMobile();


  if (!isExpanded) {
    return (
      <div className="flex h-12 items-center justify-between px-4 bg-black/40 border-b border-white/5 select-none">
        <div className="flex items-center gap-2">
          <Target className="size-3.5 text-emerald-400" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[var(--color-fg)]">Live Signals</span>
          <span className="text-[9px] font-bold text-emerald-400/60">({signals.length})</span>
        </div>
        <button onClick={() => setIsExpanded(true)} className="p-1 hover:bg-white/5 rounded">
          <ChevronDown className="size-3.5 text-[var(--color-fg-dim)]" />
        </button>
      </div>
    );
  }

  const thresholds = strategistThresholds || {
    minConfidence: 70,
    minWhaleEntrySol: 0.5,
    minBonding: 92
  };

  const fallbackRecommendations = useMemo(() => {
    const list = searchHistory || [];
    return list.map((item: any) => {
      const isFav = favorites?.includes(item.mint);
      return {
        id: `search-${item.mint}`,
        type: "scalp",
        label: isFav ? "Favorite" : "Saved",
        coin: item.symbol || "UNKNOWN",
        mint: item.mint,
        name: item.name || "",
        desc: `Recent Search: $${item.mcap ? Math.round(item.mcap).toLocaleString() : 'N/A'} MCap`,
        confidence: 100,
        bondingPct: 0,
        whaleSol: 0,
        marketCapSol: (item.mcap || 0) / 100000,
        imageUri: item.image || "",
        isDead: false,
        isPumping: false,
        isRugged: false,
      } as StrategyRecommendation;
    });
  }, [searchHistory, favorites]);

  const cols = isMobile ? 4 : 8;
  const virtualItemsList = useMemo(() => {
    const listToUse = signals.length > 0 ? signals : fallbackRecommendations;
    if (layoutMode === "lily") {
      const rows = [];
      for (let i = 0; i < listToUse.length; i += cols) {
        rows.push(listToUse.slice(i, i + cols));
      }
      return rows;
    }
    return listToUse;
  }, [signals, fallbackRecommendations, layoutMode, cols]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: virtualItemsList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => layoutMode === "lily" ? 120 : 280, // ~280px total height for the row
    overscan: 4,
  });

  return (
    <div className="flex h-full flex-col bg-[var(--color-bg-sideBar)] border-l border-[var(--color-border)] select-none">
      {/* Unified Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2">
          <Target className="size-4 text-emerald-400" />
          <span className="text-[12px] font-black uppercase tracking-widest text-white">{t("signals.title")}</span>
          <span className="text-[10px] font-bold text-emerald-400/60 ml-1">({signals.length > 0 ? signals.length : fallbackRecommendations.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Layout Mode Toggle */}
          <div className="flex items-center gap-1 bg-black/30 rounded-lg border border-white/5 p-1 shrink-0 ml-auto">
            <button
              onClick={() => setLayoutMode('standard')}
              className={`p-1.5 rounded-md transition-colors ${layoutMode === 'standard' ? 'bg-white/10 text-emerald-400' : 'text-white/40 hover:text-white/80'}`}
              title={t("signals.standardView")}
            >
              <LayoutList className="size-3.5" />
            </button>
            <button
              onClick={() => setLayoutMode('lily')}
              className={`p-1.5 rounded-md transition-colors ${layoutMode === 'lily' ? 'bg-white/10 text-emerald-400' : 'text-white/40 hover:text-white/80'}`}
              title={t("signals.lilyView")}
            >
              <LayoutGrid className="size-3.5" />
            </button>
          </div>

          {/* Collapsible filter toggle */}
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={[
              "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all select-none border",
              showFilters
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[inset_0_1px_0_rgba(16,185,129,0.1)]"
                : "bg-white/[0.03] text-[var(--color-fg-dim)] border-transparent hover:bg-white/5 hover:text-white"
            ].join(" ")}
            title="Toggle Scan Filter configuration sliders"
          >
            <Activity className={["size-2.5", showFilters ? "animate-pulse" : ""].join(" ")} />
            <span>{t("signals.filter")}</span>
          </button>
          
          <button onClick={() => setIsExpanded(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <ChevronUp className="size-4 text-[var(--color-fg-dim)]" />
          </button>
        </div>
      </div>

      {/* Threshold Configuration Drawer */}
      {showFilters && (
        <div className="p-4 space-y-4 border-b border-white/10 bg-emerald-500/5 transition-all duration-300">
          <div className="flex items-center justify-between mb-1 select-none">
             <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">{t("signals.scanFilter")}</span>
             <Activity className="size-3 text-emerald-500 animate-pulse" />
          </div>
          <ThresholdSlider 
            label={t("signals.confidence")} 
            value={thresholds.minConfidence} 
            min={0} max={100} step={5}
            unit="%"
            onChange={(v) => setStrategistThresholds({ ...thresholds, minConfidence: v })} 
          />
          <ThresholdSlider 
            label={t("signals.whaleSol")} 
            value={thresholds.minWhaleEntrySol} 
            min={0} max={5} step={0.1}
            unit=" SOL"
            onChange={(v) => setStrategistThresholds({ ...thresholds, minWhaleEntrySol: v })} 
          />
          <ThresholdSlider 
            label={t("signals.bonding")} 
            value={thresholds.minBonding} 
            min={0} max={100} step={5}
            unit="%"
            onChange={(v) => setStrategistThresholds({ ...thresholds, minBonding: v })} 
          />
        </div>
      )}

      {/* Signals List - High Density Redesign */}
      <div ref={parentRef} className="flex-1 overflow-y-auto custom-scrollbar">
        {signals.length === 0 && fallbackRecommendations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center opacity-30">
            <Zap className="size-8 text-emerald-500/20 mb-4" />
            <p className="text-[11px] text-white uppercase tracking-[0.2em] font-black leading-relaxed">
              {t("signals.monitoring")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {signals.length === 0 && (
              <div className="px-4 py-2 bg-emerald-500/5 border-b border-white/5 flex items-center justify-between shrink-0 select-none">
                <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400/80">{t("signals.favSearches")}</span>
                <span className="text-[8px] font-bold text-white/40">({fallbackRecommendations.length} {t("signals.items")})</span>
              </div>
            )}
            <div className="flex-1 relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = virtualItemsList[virtualRow.index];
                return (
                  <div
                    key={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`
                    }}
                  >
                    {layoutMode === "lily" ? (
                      <LilySignalGridRow 
                        signals={item as any[]} 
                        caMintInput={caMintInput} 
                        onOpenToken={onOpenToken}
                        onRemove={removeFromSearchHistory}
                      />
                    ) : (
                      <SignalRow 
                        signal={item as StrategyRecommendation} 
                        isSelected={caMintInput === (item as StrategyRecommendation).mint}
                        onClick={() => onOpenToken((item as StrategyRecommendation).mint)}
                        onRemove={() => removeFromSearchHistory((item as StrategyRecommendation).mint)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SignalRow({
  signal,
  isSelected,
  onClick,
  onRemove
}: {
  signal: StrategyRecommendation;
  isSelected: boolean;
  onClick: () => void;
  onRemove: () => void;
}) {
  const imageUrl = normalizeCoinImageUri(signal.imageUri, signal.mint);
  const { t } = useTranslation();

  const creationTime = "2m ago";

  // Vary image size based on market cap for visual interest
  const imageSize = signal.marketCapSol && signal.marketCapSol > 200 ? 'size-24' : signal.marketCapSol && signal.marketCapSol > 100 ? 'size-20' : signal.marketCapSol && signal.marketCapSol > 50 ? 'size-18' : signal.marketCapSol && signal.marketCapSol > 25 ? 'size-16' : 'size-14';
  const imageAnimation = `transition-all duration-700 ease-out hover:scale-125 ${signal.marketCapSol && signal.marketCapSol > 100 ? 'animate-pulse-slow' : ''} hover:shadow-2xl hover:shadow-emerald-500/40`;

  return (
    <div
      onClick={onClick}
      className={`
        group relative flex flex-col border-b border-white/5 cursor-pointer transition-all duration-300
        ${isSelected ? 'bg-emerald-500/[0.07]' : 'hover:bg-white/[0.04]'}
      `}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-2 right-2 p-1.5 hover:bg-rose-500/20 rounded-full text-white/30 hover:text-rose-400 transition-all opacity-0 group-hover:opacity-100 bg-black/60 z-20 shadow-lg border border-white/10"
        title="Remove"
      >
        <X className="size-3.5" />
      </button>
      {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 shadow-[4px_0_15px_rgba(16,185,129,0.4)]" />}

      {/* Profile layout: Chart as full backdrop, content on top */}
      <div className="flex flex-col gap-2 relative h-[250px] animate-fade-in-up">
        {/* Chart as full backdrop */}
        <div className="absolute inset-0 bg-black/40 rounded-xl overflow-hidden border border-white/5 z-0">
          <MiniCandleChart
            mint={signal.mint}
            height={250}
            rightOffset={30}
            showLogo={false}
          />
        </div>

        {/* Content overlay on top of chart */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Ticker and label at top */}
          <div className="px-3 pt-3">
            <div className="flex items-center gap-2 leading-none">
              <span className="font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent animate-gradient-x text-[16px] drop-shadow-lg">{signal.coin}</span>
              <span className={`text-[8.5px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shadow-lg border animate-pulse ${
                signal.type === 'momentum' ? 'bg-emerald-500 text-black border-emerald-400' : 'bg-blue-500 text-black border-blue-400'
              }`}>
                {signal.label}
              </span>
            </div>
          </div>

          {/* Image and stats at bottom */}
          <div className="flex-1 flex items-end px-3 pb-3 gap-3">
            {/* Token image at bottom left */}
            <div className="flex-shrink-0">
              <div className={`relative shrink-0 ${imageSize} ${imageAnimation} rounded-2xl border-2 border-white/10 shadow-2xl bg-black/40 overflow-hidden`}>
                {imageUrl ? (
                  <img src={imageUrl} className="w-full h-full rounded-2xl object-cover" alt={signal.coin} />
                ) : (
                  <div className="w-full h-full rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                    <span className="text-[14px] font-black text-emerald-400">{signal.coin.slice(0, 2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats to the right of image */}
            <div className="flex-1 min-w-0 flex flex-col justify-end gap-1.5">
              <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-[10px] leading-tight">
                <div className="flex flex-col">
                  <span className="text-white/30 uppercase font-black text-[8.5px] tracking-tighter drop-shadow-md">{t("signals.age")}</span>
                  <span className="font-mono font-bold text-amber-400 drop-shadow-md">{creationTime}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-white/30 uppercase font-black text-[8.5px] tracking-tighter drop-shadow-md">{t("signals.bonding")}</span>
                  <span className="font-mono font-bold text-emerald-400 drop-shadow-md">{signal.bondingPct}%</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-white/30 uppercase font-black text-[8.5px] tracking-tighter drop-shadow-md">{t("signals.whaleVol")}</span>
                  <span className="font-mono font-bold text-blue-400 truncate drop-shadow-md">{signal.whaleSol.toFixed(1)} SOL</span>
                </div>
              </div>

              {/* Wallet Intelligence */}
              <div className="flex items-center justify-between bg-black/60 backdrop-blur-md border border-white/10 rounded px-2 py-1">
                <span className="text-[9px] font-black text-emerald-500/60 uppercase tracking-wider">{t("signals.walletIntel")}</span>
                <span className="text-[11px] font-mono font-bold text-emerald-400 leading-none">
                  {(signal as any).walletIntelligence ? (signal as any).walletIntelligence : `${signal.confidence.toFixed(2)}%`}
                </span>
              </div>
              {signal.primaryWallet && (
                <SignalWhaleTracker signal={signal} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER: DESCRIPTION */}
      <div className="px-3 py-1 bg-black/15 border-t border-white/5 shrink-0 select-none">
         <p className="text-[9.5px] leading-normal text-white/45 truncate font-medium">
            "{signal.desc}"
         </p>
      </div>
    </div>
  );
}

function ThresholdSlider({ 
  label, 
  value, 
  min, 
  max, 
  step = 1,
  unit = "",
  onChange 
}: { 
  label: string, 
  value: number, 
  min: number, 
  max: number, 
  step?: number,
  unit?: string,
  onChange: (val: number) => void 
}) {
  return (
    <div className="flex flex-col gap-2 group">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-white/40 group-hover:text-emerald-400 transition-colors">{label}</span>
        <span className="text-[11px] font-mono font-black text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
          {value}{unit}
        </span>
      </div>
      <input 
        type="range" 
        min={min} 
        max={max} 
        step={step}
        value={value} 
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1 bg-white/5 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-400 transition-all"
      />
    </div>
  );
}

function SignalWhaleTracker({ signal }: { signal: StrategyRecommendation }) {
  const [pos, setPos] = useState<{ uiAmount: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (signal.primaryWallet && signal.mint) {
      fetchWalletPosition(signal.primaryWallet, signal.mint).then((res) => {
        if (mounted && res) setPos(res);
        if (mounted) setLoading(false);
      });
    } else {
      setLoading(false);
    }
    return () => { mounted = false; };
  }, [signal.primaryWallet, signal.mint]);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded border border-white/5 animate-pulse mt-1.5">
        <div className="size-2 rounded-full bg-emerald-500/50" />
        <span className="text-[9px] font-black text-white/40 uppercase">Tracking Whale...</span>
      </div>
    );
  }

  if (!pos || pos.uiAmount === 0) {
    return (
      <div className="flex items-center justify-between px-2 py-1 bg-red-500/10 rounded border border-red-500/20 mt-1.5">
        <span className="text-[9px] font-black text-red-400/80 uppercase">Whale Position</span>
        <span className="text-[9px] font-bold text-red-400">DUMPED</span>
      </div>
    );
  }

  const mc = signal.marketCapSol || 0;
  const supply = 1_000_000_000; // Pump fun total supply
  const currentValueSol = pos.uiAmount * (mc / supply);
  const entryCost = signal.primaryWalletEntryCost || currentValueSol;
  const multiple = entryCost > 0 ? (currentValueSol / entryCost).toFixed(2) : "1.00";
  const pnlColor = Number(multiple) >= 1.5 ? "text-emerald-400" : Number(multiple) < 0.8 ? "text-red-400" : "text-amber-400";

  return (
    <div className="flex items-center justify-between px-2 py-1 bg-emerald-500/10 rounded border border-emerald-500/20 mt-1.5">
      <div className="flex flex-col">
        <span className="text-[8px] font-black text-emerald-500/60 uppercase">Whale Bag</span>
        <span className="text-[10px] font-mono font-bold text-white">{(pos.uiAmount / 1_000_000).toFixed(1)}M</span>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-[8px] font-black text-emerald-500/60 uppercase">PnL</span>
        <span className={`text-[10px] font-mono font-bold ${pnlColor}`}>{multiple}x</span>
      </div>
    </div>
  );
}
